/**
 * The newest published release of each app, as the release manifest says.
 *
 * Read from the static `/downloads/manifest.json` — the snapshot `package-apps.sh`
 * and `publish-release.sh` regenerate whenever a release is published — rather than
 * from `/api/releases`, which is the endpoint the Orobas Installer's updater reads.
 * The split is deliberate: the manifest is the copy for humans looking at a page, the
 * API is the copy a program has to ask on every run, and neither depends on the
 * other being current at the same moment.
 *
 * `null` is "still loading"; `[]` is "asked and failed", which every consumer
 * renders as no version line rather than as an error state — the downloads work
 * without the version being printed, and the installer's own updater is the thing
 * that actually has to refuse on a failed release read.
 */

import { useEffect, useState } from 'react';

/** One app's newest published build, in the manifest shape the installer reads. */
export interface ReleaseEntry {
  readonly id: string;
  readonly version: string;
  readonly bundle_name: string;
  readonly url: string;
  readonly bytes: number;
  readonly sha256: string;
}

export function useReleases(): readonly ReleaseEntry[] | null {
  const [releases, setReleases] = useState<readonly ReleaseEntry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchReleaseManifest()
      .then((products) => {
        if (!cancelled) setReleases(products);
      })
      .catch(() => {
        if (!cancelled) setReleases([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return releases;
}

/** The newest release of one app, when the manifest carries it. */
export function releaseVersionFor(
  releases: readonly ReleaseEntry[] | null,
  appId: string,
): string | undefined {
  return releases?.find((release) => release.id === appId)?.version;
}

/**
 * Reads the manifest, and keeps only entries that are actually a release.
 *
 * Validated field by field rather than cast: a truncated or hand-edited manifest is
 * a real possibility, and a line with no version would render as `Version {version}`
 * — the placeholder printed literally — which is worse than the entry being dropped.
 */
async function fetchReleaseManifest(): Promise<readonly ReleaseEntry[]> {
  const response = await fetch('/downloads/manifest.json', { credentials: 'omit' });
  if (!response.ok) throw new Error(`manifest: HTTP ${response.status}`);

  const body: unknown = await response.json();
  if (typeof body !== 'object' || body === null) throw new Error('manifest: not an object');
  const products = (body as { products?: unknown })['products'];
  if (!Array.isArray(products)) throw new Error('manifest: products is not a list');

  return products.filter((entry): entry is ReleaseEntry => {
    if (typeof entry !== 'object' || entry === null) return false;
    const candidate = entry as Partial<ReleaseEntry>;
    return (
      typeof candidate.id === 'string' &&
      candidate.id !== '' &&
      typeof candidate.version === 'string' &&
      candidate.version !== '' &&
      typeof candidate.url === 'string' &&
      typeof candidate.sha256 === 'string'
    );
  });
}
